import type { Rule } from './types';

// Friendly labels for style metrics surfaced in finding messages.
const LABEL: Record<string, string> = {
  descChars: 'description length',
  bodyWords: 'body length',
  sentenceAvg: 'average sentence length',
  imperativeRatio: 'imperative-mood ratio',
  passiveRatio: 'passive-voice ratio',
  hedgeDensity: 'hedge density',
  fillerDensity: 'filler density',
  uniqueTokenRatio: 'vocabulary diversity',
  codeToProse: 'code-to-prose ratio',
  headingDensity: 'heading density',
  bulletDensity: 'bullet density',
  decisionDensity: 'decision-logic density',
  validationDensity: 'validation density',
};

export const styleRules: Rule[] = [
  {
    id: 'lexical.style-fingerprint',
    category: 'lexical',
    severity: 'info',
    weight: 4,
    title: 'Style fingerprint near reference centroid',
    appliesTo: 'both',
    run(ctx) {
      if (ctx.bodyText.wordCount < 150) {
        return { status: 'skipped', message: 'Body too short for fingerprint analysis.' };
      }
      const max = ctx.style.maxAbsZ;
      const mean = ctx.style.meanAbsZ;
      const outliers = ctx.style.outliers;

      if (outliers.length === 0 && mean < 0.8) {
        return { status: 'pass', message: `Style centered (mean z=${mean.toFixed(2)}).` };
      }
      if (outliers.length === 0) {
        return { status: 'pass', message: `Style near centroid (mean z=${mean.toFixed(2)}, max z=${max.toFixed(1)}).` };
      }

      const headline = outliers.slice(0, 3).map((o) => {
        const label = LABEL[o.metric] ?? o.metric;
        const direction = o.direction === 'above' ? 'high' : 'low';
        return `${label} unusually ${direction} (z=${o.z.toFixed(1)})`;
      }).join(' · ');

      const severity = outliers.length >= 3 || max >= 3 ? 'warn' : 'pass';
      return {
        status: severity,
        message: `${outliers.length} stylistic outlier(s): ${headline}.`,
        fix: severity === 'warn'
          ? 'Bring outlier metrics toward the centroid. Polished skills cluster tightly on these features.'
          : undefined,
      };
    },
  },
];
